import { NoticeBox } from "../../ui";
import { formatSar } from "../../../utils/billingMoney";
import { aiWalletMenuText, aiWalletRechargePath, shortageSentence } from "./aiWalletMenu";
import "./AiWallet.css";

// What an AI feature (AI Menu Import, Menu Copilot) shows when the AI Wallet
// of the restaurant refuses an operation. The backend answers with a typed
// error and says what THIS user may see:
//
//   err.code                          INSUFFICIENT_AI_BALANCE | AI_WALLET_FROZEN | AI_PRICING_NOT_ACTIVE
//   err.details.wallet.canViewBalance the available balance may be shown
//   err.details.wallet.canAddFunds    the user may add funds (link to Billing)
//   err.details.wallet.availableBalanceMinor / requiredAmountMinor / shortageMinor
//                                     insufficient balance, for users who may
//                                     see the wallet (computed by the backend)
//
// Staff without billing access only learn that the balance is insufficient
// and who can fix it. Nothing here is about providers, models or pricing.

const CODES = ["INSUFFICIENT_AI_BALANCE", "AI_WALLET_FROZEN", "AI_PRICING_NOT_ACTIVE"];

const TEXT = {
  ar: {
    INSUFFICIENT_AI_BALANCE: {
      title: "رصيد محفظة الذكاء الاصطناعي غير كافٍ",
      owner: "رصيد الذكاء الاصطناعي لا يكفي لتنفيذ هذه العملية. أضف رصيداً للمتابعة.",
      viewer: "رصيد الذكاء الاصطناعي لا يكفي لتنفيذ هذه العملية.",
      staff: "رصيد الذكاء الاصطناعي غير كافٍ. تواصل مع مالك حساب المطعم.",
    },
    AI_WALLET_FROZEN: {
      title: "محفظة الذكاء الاصطناعي موقوفة",
      owner: "محفظة الذكاء الاصطناعي لهذا المطعم موقوفة مؤقتاً. تواصل مع دعم لُد.",
      viewer: "محفظة الذكاء الاصطناعي لهذا المطعم موقوفة مؤقتاً. تواصل مع دعم لُد.",
      staff: "الذكاء الاصطناعي غير متاح لهذا المطعم حالياً. تواصل مع مالك حساب المطعم.",
    },
    AI_PRICING_NOT_ACTIVE: {
      title: "الذكاء الاصطناعي غير متاح مؤقتاً",
      owner: "الذكاء الاصطناعي غير متاح مؤقتاً. حاول مرة أخرى لاحقاً.",
      viewer: "الذكاء الاصطناعي غير متاح مؤقتاً. حاول مرة أخرى لاحقاً.",
      staff: "الذكاء الاصطناعي غير متاح مؤقتاً. حاول مرة أخرى لاحقاً.",
    },
    balance: "الرصيد المتاح",
    // A failed import shown later, to whoever opens it.
    draft: {
      INSUFFICIENT_AI_BALANCE: "رصيد الذكاء الاصطناعي لم يكن كافياً لمعالجة هذا الملف. بعد إضافة رصيد من صفحة الفوترة يمكن إعادة المحاولة.",
      AI_WALLET_FROZEN: "محفظة الذكاء الاصطناعي لهذا المطعم موقوفة مؤقتاً، فلم تتم معالجة الملف.",
      AI_PRICING_NOT_ACTIVE: "الذكاء الاصطناعي غير متاح مؤقتاً. حاول مرة أخرى لاحقاً.",
    },
  },
  en: {
    INSUFFICIENT_AI_BALANCE: {
      title: "Insufficient AI Wallet balance",
      owner: "The AI balance is not enough for this operation. Add funds to continue.",
      viewer: "The AI balance is not enough for this operation.",
      staff: "AI balance is insufficient. Contact the restaurant account owner.",
    },
    AI_WALLET_FROZEN: {
      title: "AI Wallet frozen",
      owner: "The AI Wallet of this restaurant is frozen. Contact LUD support.",
      viewer: "The AI Wallet of this restaurant is frozen. Contact LUD support.",
      staff: "AI is not available for this restaurant right now. Contact the restaurant account owner.",
    },
    AI_PRICING_NOT_ACTIVE: {
      title: "AI temporarily unavailable",
      owner: "AI is temporarily unavailable. Try again later.",
      viewer: "AI is temporarily unavailable. Try again later.",
      staff: "AI is temporarily unavailable. Try again later.",
    },
    balance: "Available balance",
    draft: {
      INSUFFICIENT_AI_BALANCE: "The AI balance was not enough to process this file. Once funds are added in Billing, it can be retried.",
      AI_WALLET_FROZEN: "The AI Wallet of this restaurant is frozen, so the file was not processed.",
      AI_PRICING_NOT_ACTIVE: "AI is temporarily unavailable. Try again later.",
    },
  },
};

const textFor = (lang) => (lang === "en" ? TEXT.en : TEXT.ar);

export const isAiWalletError = (error) => CODES.includes(error?.code);

// The part of a refused request the notice needs (kept in component state).
export const aiWalletNoticeOf = (error) => (isAiWalletError(error) ? { code: error.code, wallet: error.details?.wallet || {} } : null);

const audienceOf = (wallet = {}) => (wallet.canAddFunds ? "owner" : wallet.canViewBalance ? "viewer" : "staff");

// One line for places that can only show text; "" when it is not a wallet error.
export const aiWalletErrorText = (error, lang) => (isAiWalletError(error) ? shortageSentence(error.details?.wallet, lang) || textFor(lang)[error.code][audienceOf(error.details?.wallet)] : "");

// The notice of an import draft whose run the wallet refused (draft.walletError).
export const aiWalletDraftNoticeOf = (draft) => (draft?.walletError?.code ? { code: draft.walletError.code, wallet: draft.walletError.wallet || {}, draft: true } : null);

// Localized reason of an import that failed because of the wallet; null for other failures.
export const aiWalletDraftFailure = (code, lang) => textFor(lang).draft[code] || null;

export default function AiBalanceNotice({ notice, lang = "ar", size = "sm" }) {
  if (!notice || !CODES.includes(notice.code)) return null;
  const text = textFor(lang);
  const menuText = aiWalletMenuText(lang);
  const wallet = notice.wallet || {};
  const audience = audienceOf(wallet);
  const showBalance = Boolean(wallet.canViewBalance) && Number.isFinite(Number(wallet.balanceMinor));
  const insufficient = notice.code === "INSUFFICIENT_AI_BALANCE";
  // Balance, required amount and shortage, exactly as the backend computed them.
  const sentence = insufficient ? shortageSentence(wallet, lang) : "";
  const hasShortage = Boolean(sentence);
  const canAdd = Boolean(wallet.canAddFunds) && insufficient;
  const message = sentence || (notice.draft && text.draft[notice.code]) || text[notice.code][audience];
  return (
    <div className="aiw-notice" role="alert" dir={lang === "ar" ? "rtl" : "ltr"} data-testid="ai-wallet-notice">
      <NoticeBox variant={notice.code === "AI_PRICING_NOT_ACTIVE" ? "info" : "warning"} size={size} title={text[notice.code].title}>
        <p className="aiw-notice-text">{message}</p>
        {hasShortage && (
          <dl className="aiw-notice-facts">
            <div>
              <dt>{menuText.available}</dt>
              <dd dir="ltr">{formatSar(wallet.availableBalanceMinor, lang)}</dd>
            </div>
            <div>
              <dt>{menuText.required}</dt>
              <dd dir="ltr">{formatSar(wallet.requiredAmountMinor, lang)}</dd>
            </div>
            <div className={Number(wallet.shortageMinor) > 0 ? "is-short" : ""}>
              <dt>{menuText.shortage}</dt>
              <dd dir="ltr">{formatSar(wallet.shortageMinor, lang)}</dd>
            </div>
          </dl>
        )}
        {((showBalance && !hasShortage) || canAdd) && (
          <div className="aiw-notice-row">
            {showBalance && !hasShortage && (
              <span className="aiw-notice-balance">
                {text.balance}: <strong dir="ltr">{formatSar(wallet.balanceMinor, lang)}</strong>
              </span>
            )}
            {canAdd && (
              <a className="ui-button primary filled sm rounded aiw-notice-action" href={aiWalletRechargePath(wallet.storeId)}>
                {menuText.recharge}
              </a>
            )}
          </div>
        )}
      </NoticeBox>
    </div>
  );
}
